/**
 * @file AssetService.ts
 * @role The ONLY owner of Pixi asset loading/retrieval. Wraps Pixi.Assets, registers
 *       manifest bundles, loads them with progress reporting, and serves assets by ID.
 *       This is a v1 shell: real bundles are filled in at later milestones.
 * @owns Asset lifecycle: init, bundle registration, load, get.
 * @reads AssetManifest. Emits progress on the EventBus.
 * @neverDoes Let any other class touch Pixi.Assets. Decide layout. Run game logic.
 * @invariants 15 (no Pixi.Assets access outside AssetService).
 * @see docs/CONFIG_GUIDE.md
 */
import { Assets } from 'pixi.js';
import type { EventBus } from '@core/events/EventBus';
import { GameError } from '@core/errors/GameError';
import type { AssetBundleName } from './AssetBundleName';
import { ASSET_MANIFEST, type AssetManifest } from './AssetManifest';
import type { TextureResolutionPreference } from './TextureResolutionPolicy';

export interface LoadingProgress {
  bundleName: string;
  progress01: number;
}

export class AssetService {
  private initialized = false;
  private readonly registeredBundles = new Set<AssetBundleName>();
  private readonly loadedBundles = new Set<AssetBundleName>();
  private readonly loadedAssetIds = new Set<string>();
  private readonly assetIdsByBundle = new Map<AssetBundleName, ReadonlySet<string>>();

  constructor(
    private readonly eventBus: EventBus,
    private readonly manifest: AssetManifest = ASSET_MANIFEST,
  ) {}

  /** Initialize Pixi.Assets and register every non-empty bundle from the manifest. */
  async init(preferredTextureResolution: TextureResolutionPreference = 1): Promise<void> {
    if (this.initialized) return;
    await Assets.init({ texturePreference: { resolution: preferredTextureResolution } });
    for (const [manifestName, assets] of Object.entries(this.manifest)) {
      if (assets && Object.keys(assets).length > 0) {
        const name = manifestName as AssetBundleName;
        Assets.addBundle(name, assets);
        this.registeredBundles.add(name);
        this.assetIdsByBundle.set(name, this.collectAssetIds(assets));
      }
    }
    this.initialized = true;
  }

  /**
   * Load a bundle, emitting progress. Unregistered/empty bundles resolve immediately
   * (the shell tolerates milestones whose art does not exist yet).
   */
  async loadBundle(name: AssetBundleName): Promise<void> {
    this.assertInitialized();
    if (this.loadedBundles.has(name)) {
      this.emitProgress(name, 1);
      return;
    }
    if (!this.registeredBundles.has(name)) {
      this.loadedBundles.add(name);
      this.emitProgress(name, 1);
      return;
    }
    try {
      await Assets.loadBundle(name, (progress01: number) => this.emitProgress(name, progress01));
      this.loadedBundles.add(name);
      for (const assetId of this.assetIdsByBundle.get(name) ?? []) {
        this.loadedAssetIds.add(assetId);
      }
      this.emitProgress(name, 1);
    } catch (cause) {
      throw new GameError('ASSET_BUNDLE_FAILED', `Failed to load asset bundle: ${name}`, {
        recoverable: true,
        cause,
      });
    }
  }

  /** Whether an asset's owning bundle has completed loading successfully. */
  hasLoaded(assetId: string): boolean {
    return this.initialized && this.loadedAssetIds.has(assetId);
  }

  /**
   * Retrieve an asset only when its bundle has loaded. This avoids asking Pixi for
   * uncached optional assets, which would otherwise produce cache-miss warnings.
   */
  getOptional<T = unknown>(assetId: string): T | undefined {
    if (!this.hasLoaded(assetId)) return undefined;
    return Assets.get<T | undefined>(assetId);
  }

  /** Retrieve a required loaded asset by its manifest ID, failing loudly when unavailable. */
  get<T = unknown>(assetId: string): T {
    this.assertInitialized();
    if (!this.loadedAssetIds.has(assetId)) {
      throw new GameError('UNKNOWN_ERROR', `Required asset has not been loaded: ${assetId}`);
    }
    const asset = Assets.get<T | undefined>(assetId);
    if (asset === undefined) {
      throw new GameError('UNKNOWN_ERROR', `Required asset is missing from the cache: ${assetId}`);
    }
    return asset;
  }

  private collectAssetIds(
    assets: NonNullable<AssetManifest[AssetBundleName]>,
  ): ReadonlySet<string> {
    const assetIds = new Set<string>();
    for (const [assetId, descriptor] of Object.entries(assets)) {
      assetIds.add(assetId);
      if (typeof descriptor === 'string' || descriptor.alias === undefined) continue;
      const aliases = typeof descriptor.alias === 'string' ? [descriptor.alias] : descriptor.alias;
      for (const alias of aliases) assetIds.add(alias);
    }
    return assetIds;
  }

  private emitProgress(bundleName: string, progress01: number): void {
    this.eventBus.emit('assets:bundle_progress', { bundleName, progress01 });
  }

  private assertInitialized(): void {
    if (!this.initialized) {
      throw new GameError('UNKNOWN_ERROR', 'AssetService used before init()');
    }
  }
}
